import {
  readNarrativePrefix,
  upsertNarrativePrefix,
  deleteNarrativePrefix,
  PrefixGlobalKey,
} from "pages/api-clients/narrative-prefix";
import { NarrativePrefixType } from "pages/api/settings/narrative-prefix/types";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useEffectOnce } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

const DEBUG = true;

function log(...args: any[]) {
  if (DEBUG) {
    console.log("[usePromptInstructions]", ...args);
  }
}

export function usePromptInstructions() {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [prefixes, setPrefixes] = useState<NarrativePrefixType[]>([]);

  // Log state changes
  useEffect(() => {
    log("State updated:", { loading, error, prefixesCount: prefixes.length });
  }, [loading, error, prefixes]);

  const refresh = useCallback(async (globalKey?: PrefixGlobalKey) => {
    log("Refresh called with globalKey:", globalKey);
    setLoading(true);
    setError(undefined);
    try {
      log("Fetching narrative prefix...");
      const response = await readNarrativePrefix(globalKey);
      log("Received response:", { responseLength: response.length });

      if (response.length > 0) {
        log("Setting prefixes from response");
        setPrefixes(response);
      } else {
        log("No prefixes found, setting default");
        //default
        setPrefixes([
          {
            id: uuidv4(),
            prefix: "",
            name: "Narrative",
            global_key: "narrative",
            created_at: Date.now().toString(),
            updated_at: Date.now().toString(),
          },
        ]);
      }
    } catch (error) {
      log("Error loading narrative prefix:", error);
      setError(`Error loading narrative prefix ${error}`);
      // Set default prefix on error to prevent empty state
      log("Setting default prefix due to error");
      setPrefixes([
        {
          id: uuidv4(),
          prefix: "",
          name: "Narrative",
          global_key: "narrative",
          created_at: Date.now().toString(),
          updated_at: Date.now().toString(),
        },
      ]);
    } finally {
      log("Refresh complete, setting loading to false");
      setLoading(false);
    }
  }, []);

  const save = useCallback(
    async (prefix: Omit<NarrativePrefixType, "created_at" | "updated_at">) => {
      log("Save called with prefix:", prefix);
      try {
        setLoading(true);
        const uuid = prefix.id || uuidv4();
        prefix.id = uuid;
        log("Saving prefix with id:", uuid);
        await upsertNarrativePrefix(prefix);
        setError(undefined);
      } catch (error) {
        log("Error saving prefix:", error);
        setError("Error saving narrative prefix");
      } finally {
        log("Save complete, refreshing prefixes");
        refresh();
      }
    },
    [refresh]
  );

  const remove = useCallback(
    async (id: string) => {
      log("Remove called with id:", id);
      try {
        setLoading(true);
        log("Deleting prefix");
        await deleteNarrativePrefix(id);
        setError(undefined);
      } catch (error) {
        log("Error deleting prefix:", error);
        setError("Error deleting narrative prefix");
      } finally {
        log("Remove complete, refreshing prefixes");
        refresh();
      }
    },
    [refresh]
  );

  useEffectOnce(() => {
    log("Initial load triggered");
    ``;
    refresh();
  });

  return useMemo(() => {
    log("Memoized value updated");
    return {
      prefixes,
      refresh,
      save,
      remove,
      loading,
      error,
    };
  }, [prefixes, refresh, save, remove, loading, error]);
}
